import {UpdateLensAction} from './../../core/store/lenses/lenses.action';
import {
  ListViewWarningDialogComponent,
  ListViewWarningDialogData,
} from './../../shared/list-views/shared/warning-dialog/list-view-warning-dialog.component';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
  OnDestroy,
} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {MatTable, MatTableDataSource, MatTableModule} from '@angular/material/table';
import {MatIconModule} from '@angular/material/icon';
import {CommonModule} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {Store} from '@ngrx/store';

import {Lens} from '../shared/model/lens';
import {ListViewsModule} from './../../shared/list-views/list-views.module';
import {OphSlideToggleComponent} from './../../shared/design/oph-slide-toggle/oph-slide-toggle.component';
import {MessageService} from './../../services/message.service';
import {LensDialogData, LenseDialogComponent} from '../lense-dialog/lense-dialog.component';
import {LENS_DIALOG_SETTINGS} from '../utils/lenses.constants';
import {DeleteLensAction, GetLensesAction} from 'src/app/core/store/lenses/lenses.action';
import {MobileService} from 'src/app/core/page/mobile.service';

// Table row type
interface LensSettingRow {
  key: string; // settingId or key
  name: string;
  description?: string;
  lenses: {
    [lensTitle: string]: {
      value: boolean;
      lensId: string;
      settingId: string;
    };
  };
}

@Component({
  selector: 'lenses-table',
  standalone: true,
  imports: [MatTableModule, MatIconModule, CommonModule, OphSlideToggleComponent, ListViewsModule],
  templateUrl: './lenses-table.component.html',
  styleUrl: './lenses-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LensesTableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly mobileService = inject(MobileService);
  private messageService = inject(MessageService);
  private dialog = inject(MatDialog);
  private store$ = inject(Store);
  searchValue$ = input<BehaviorSubject<string>>();
  allLenses = input.required<Lens[]>();

  displayedColumns: string[] = ['setting'];
  dataSource = new MatTableDataSource<LensSettingRow>([]);
  dataSource$ = new BehaviorSubject<LensSettingRow[]>([]);
  @ViewChild('lensesTableEl') lensesTableEl: MatTable<LensSettingRow>;
  menuOptions = ['edit', 'delete'];
  isLoading = signal<boolean>(false);
  defaultLensColumnTitle = 'Default';

  ngOnInit(): void {
    this.subscribeToDataSource();
    this.subscribeToSearchValue();
    this.prepareSettingsTable();
    this.setTableFilterPredicate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.allLenses) {
      this.prepareSettingsTable();
    }
  }

  ngOnDestroy(): void {
    if (this.dataSource$) {
      this.dataSource$?.unsubscribe();
    }
  }

  subscribeToDataSource(): void {
    this.dataSource$.subscribe(rolePermissionRows => {
      this.dataSource.data = rolePermissionRows;
    });
  }

  subscribeToSearchValue(): void {
    this.searchValue$().subscribe((searchValue: string) => {
      this.dataSource.filter = searchValue.trim().toLowerCase();
    });
  }

  prepareSettingsTable(): void {
    const lenses = this.allLenses();
    if (!lenses) return;

    const settingMap = new Map<string, LensSettingRow>();

    for (const lens of lenses) {
      // settings is always an object: { [settingId]: { name, description, active } }
      if (typeof lens.settings === 'object' && lens.settings !== null) {
        for (const [settingKey, settingObj] of Object.entries(lens.settings)) {
          const key = settingKey;
          if (!settingMap.has(key)) {
            settingMap.set(key, {
              key,
              name: settingObj.name || key,
              description: settingObj.description,
              lenses: {},
            });
          }
          const row = settingMap.get(key);
          if (row) {
            row.lenses[lens.title] = {
              value: settingObj.active === true,
              lensId: lens._id,
              settingId: key,
            };
          }
        }
      }
    }

    this.dataSource$.next(Array.from(settingMap.values()));
    this.setTableColumns();
  }

  setTableFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: LensSettingRow, filter: string) => {
      const searchTerm = filter.toLowerCase();
      const matchesName = data.name.toLowerCase().includes(searchTerm);
      const matchesDescription = data.description.toLowerCase().includes(searchTerm);
      const matchesLens = Object.keys(data.lenses).some(lensName => lensName.toLowerCase().includes(searchTerm));
      return matchesName || matchesDescription || matchesLens;
    };
  }

  setTableColumns(): void {
    const lenses = this.allLenses();
    this.displayedColumns = ['setting', ...lenses.map(l => l.title)];
  }

  toggleSetting(lensTitle: string, row: LensSettingRow, checked: boolean): void {
    this.isLoading.set(true);

    const lens = this.allLenses().find(r => r.title.toLowerCase() === lensTitle.toLowerCase());
    if (!lens) {
      this.isLoading.set(false);
      this.messageService.add(`Lens ${lensTitle} not found`, 'error');
      return;
    }

    const {key: settingKey} = row;
    const settings = lens.settings;
    const lensSetting = settings[settingKey];
    lensSetting.active = checked;
    const updatedLensPayload = {...lens, settings};

    this.store$.dispatch(
      new UpdateLensAction({
        lens: updatedLensPayload,
        onSuccess: () => {
          this.isLoading.set(false);
          this.messageService.add(`Lens updated successfully`, 'newTaskAssigned');
          this.store$.dispatch(new GetLensesAction({}));
        },
        onFailure: error => {
          this.isLoading.set(false);
          this.messageService.add(`Error updating lens`, 'error');
          console.error('Error updating lens:', error);
        },
      })
    );
  }

  onMenu(type: 'edit' | 'delete', lensName: string): void {
    const selectedLens = this.allLenses().find(l => l.title.toLowerCase() === lensName.toLowerCase());
    if (!selectedLens) {
      this.messageService.add(`Lens ${lensName} not found`, 'error');
      return;
    }
    if (selectedLens.title.toLowerCase() === this.defaultLensColumnTitle.toLowerCase()) {
      this.messageService.add('Cannot edit or delete the default lens', 'error');
      return;
    }
    if (type === 'edit') {
      this.editLens(selectedLens);
    }
    if (type === 'delete') {
      this.deleteLens(selectedLens);
    }
  }

  editLens(lens: Lens): void {
    this.dialog.open(LenseDialogComponent, {
      data: {lens: lens, lenses: this.allLenses()} as LensDialogData,
      ...LENS_DIALOG_SETTINGS,
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
    });
  }

  deleteLens(lens: Lens): void {
    const dialog = this.dialog.open(ListViewWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'list-views-warning-dialog',
      data: {
        type: 'custom',
        customData: {
          title: 'Are you sure you want to permanently delete the following lens?',
          message: `Lens: "${lens.title}". This action cannot be undone.`,
          confirmText: 'Yes, Delete',
          cancelText: 'Cancel',
        },
      } as ListViewWarningDialogData,
    });

    dialog.afterClosed().subscribe(confirm => {
      if (confirm) {
        this.store$.dispatch(
          new DeleteLensAction({
            id: lens._id,
            onSuccess: () => {
              this.store$.dispatch(new GetLensesAction({}));
              this.messageService.add(`Lens "${lens.title}" deleted successfully`, 'taskCompleted');
            },
            onFailure: (error: Error) => {
              this.messageService.add(`Error deleting lens "${lens.title}": ${error.message}`, 'error');
            },
          })
        );
      }
    });
  }

  trackByFn(index: number, row: LensSettingRow): string | number {
    return row.key || index;
  }
}
